import { Controller } from '@hotwired/stimulus'

/**
 * Page Regenerate Controller
 * Handles individual page regeneration from the admin order page.
 * Uses fetch to trigger regeneration and shows loading state.
 */
export default class extends Controller {
  static targets = ['button', 'icon', 'spinner']

  static values = {
    websiteId: String
  }

  async regenerate(event) {
    const button = event.currentTarget
    const url = button.dataset.url
    const pageTitle = button.dataset.pageTitle

    if (!url) {
      console.error('[PageRegenerate] No URL provided for regeneration')
      return
    }

    // Confirm before regenerating
    const confirmed = window.confirm(`Regenerate "${pageTitle}"? This will overwrite the current content.`)
    if (!confirmed) return

    // Show loading state
    this.setLoading(button, true)

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': this.csrfToken,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      })

      const data = await response.json()

      if (response.ok && data.success) {
        this.showSuccess(button, pageTitle)
        // Reload to show updated content after a brief delay
        setTimeout(() => window.location.reload(), 1500)
      } else {
        this.showError(button, data.error || 'Regeneration failed')
        this.setLoading(button, false)
      }
    } catch (error) {
      console.error('[PageRegenerate] Error:', error)
      this.showError(button, 'Network error')
      this.setLoading(button, false)
    }
  }

  setLoading(button, loading) {
    const icon = button.querySelector('[data-admin--page-regenerate-target="icon"]')
    const spinner = button.querySelector('[data-admin--page-regenerate-target="spinner"]')

    if (loading) {
      button.disabled = true
      if (icon) icon.classList.add('hidden')
      if (spinner) spinner.classList.remove('hidden')
    } else {
      button.disabled = false
      if (icon) icon.classList.remove('hidden')
      if (spinner) spinner.classList.add('hidden')
    }
  }

  showSuccess(button, _pageTitle) {
    // Briefly show a success state
    const icon = button.querySelector('[data-admin--page-regenerate-target="icon"]')
    const spinner = button.querySelector('[data-admin--page-regenerate-target="spinner"]')

    if (spinner) spinner.classList.add('hidden')
    if (icon) {
      icon.classList.remove('hidden')
      icon.style.color = 'var(--color-success-600)'
    }
  }

  showError(button, message) {
    // Show error toast/alert
    const alertDiv = document.createElement('div')
    alertDiv.className = 'fixed bottom-4 right-4 z-50 alert alert-error shadow-lg'
    alertDiv.innerHTML = `
      <div class="alert-icon">
        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
        </svg>
      </div>
      <div class="alert-content">
        <p class="alert-description">${message}</p>
      </div>
    `
    document.body.appendChild(alertDiv)

    // Remove after 5 seconds
    setTimeout(() => alertDiv.remove(), 5000)
  }

  get csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ''
  }
}
